import styled from '@emotion/styled';

// Styled components and the usage-stats panel for CorpusTietopohjaV4Screen.jsx
// (specs/rag-source-heatmap.md — v4), split out to keep the screen file under
// the 1000-line limit.

export const MONO_FONT = "'JetBrains Mono', 'SF Mono', monospace";

export const Container = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;
  background: radial-gradient(ellipse at 50% 40%, #14141f 0%, #0a0a12 60%, #050508 100%);
  color: white;
  position: relative;
  overflow: hidden;
`;

export const SVGContainer = styled.svg`
  width: 100%;
  height: 100%;
  display: block;
  touch-action: none;
  cursor: grab;
  &:active { cursor: grabbing; }
`;

export const TopLeftBar = styled.div`
  position: absolute;
  top: 20px;
  left: 24px;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: calc(100% - 48px);
`;

export const Title = styled.h1`
  color: rgba(255, 255, 255, 0.85);
  font-family: ${MONO_FONT};
  font-size: 0.95rem;
  font-weight: 500;
  letter-spacing: -0.01em;
  margin: 0;
  pointer-events: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const InstanceSelect = styled.select`
  font-family: ${MONO_FONT};
  font-size: 0.72rem;
  letter-spacing: 0.02em;
  padding: 5px 10px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(20, 20, 28, 0.6);
  color: rgba(255, 255, 255, 0.75);
  cursor: pointer;
  max-width: 220px;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.4);
  }

  option {
    background: #14141f;
    color: #fff;
  }
`;

export const TopRightControls = styled.div`
  position: absolute;
  top: 20px;
  right: 24px;
  z-index: 10;
  display: flex;
  gap: 2px;
  background: rgba(20, 20, 28, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 20px;
  padding: 3px;
`;

export const ModeButton = styled.button`
  font-family: ${MONO_FONT};
  font-size: 0.72rem;
  letter-spacing: 0.03em;
  padding: 6px 14px;
  border-radius: 16px;
  cursor: pointer;
  border: none;
  background: ${props => (props.$active ? 'rgba(255,255,255,0.16)' : 'transparent')};
  color: ${props => (props.$active ? '#fff' : 'rgba(255,255,255,0.55)')};
`;

export const FilterBar = styled.div`
  position: absolute;
  bottom: 20px;
  left: 24px;
  z-index: 10;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-width: calc(100% - 48px);
`;

export const FilterChip = styled.button`
  font-family: ${MONO_FONT};
  font-size: 0.68rem;
  letter-spacing: 0.03em;
  padding: 4px 11px;
  border-radius: 12px;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: ${props => (props.$active ? 'rgba(255,255,255,0.18)' : 'rgba(10,10,16,0.5)')};
  color: ${props => (props.$active ? '#fff' : 'rgba(255,255,255,0.55)')};
`;

export const StyleSwitcher = styled.div`
  position: absolute;
  bottom: 20px;
  right: 24px;
  z-index: 10;
  display: flex;
  gap: 2px;
  background: rgba(20, 20, 28, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  padding: 3px;
`;

export const StyleButton = styled.button`
  font-family: ${MONO_FONT};
  font-size: 1rem;
  line-height: 1;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  cursor: pointer;
  border: none;
  background: ${props => (props.$active ? 'rgba(255,255,255,0.16)' : 'transparent')};
  color: ${props => (props.$active ? '#fff' : 'rgba(255,255,255,0.5)')};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ListOrderBar = styled.div`
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  display: flex;
  gap: 2px;
  background: rgba(20, 20, 28, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 16px;
  padding: 3px;
`;

export const ListOrderButton = styled.button`
  font-family: ${MONO_FONT};
  font-size: 0.7rem;
  letter-spacing: 0.03em;
  padding: 6px 12px;
  border-radius: 13px;
  cursor: pointer;
  border: none;
  background: ${props => (props.$active ? 'rgba(255,255,255,0.16)' : 'transparent')};
  color: ${props => (props.$active ? '#fff' : 'rgba(255,255,255,0.55)')};
`;

export const StatusMessage = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: rgba(255, 255, 255, 0.55);
  font-family: ${MONO_FONT};
  font-size: 1rem;
  text-align: center;
`;

const StatsPanel = styled.div`
  position: absolute;
  top: 64px;
  left: 24px;
  z-index: 10;
  width: 230px;
  background: rgba(10, 10, 16, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 10px;
  padding: 14px 16px;
  font-family: ${MONO_FONT};
`;

const StatsTitle = styled.div`
  font-size: 0.66rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.45);
  margin-bottom: 10px;
`;

const StatsBarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const StatsBarLabel = styled.div`
  font-size: 0.62rem;
  color: rgba(255, 255, 255, 0.7);
  width: 76px;
  flex-shrink: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatsBarTrack = styled.div`
  flex: 1;
  height: 6px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 3px;
  overflow: hidden;
`;

const StatsBarFill = styled.div`
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;
`;

const StatsBarValue = styled.div`
  font-size: 0.62rem;
  color: rgba(255, 255, 255, 0.5);
  width: 24px;
  text-align: right;
  flex-shrink: 0;
`;

// Reactive descriptive stats — pure React, driven by mode/teemaFilter state,
// so it re-renders on its own whenever the filter selection changes. Color
// reuses the SAME cluster colors the canvas already uses for identity ("color
// follows the entity") rather than a new palette, so no separate legend and
// no new palette to validate (dataviz skill) — the canvas itself is the legend.
export function UsageStatsPanel({ data, teemaFilter, t }) {
  const isFilteredOut = (n) => teemaFilter.size > 0 && n.teema && !teemaFilter.has(n.teema);
  const visible = data.nodes.filter(n => !isFilteredOut(n));
  const sums = {};
  data.clusterOrder.forEach(key => { sums[key] = 0; });
  visible.forEach(n => { sums[n.cluster] = (sums[n.cluster] || 0) + n.breadth; });
  const max = Math.max(1, ...Object.values(sums));
  const retrievedCount = visible.filter(n => n.retrieved).length;

  return (
    <StatsPanel>
      <StatsTitle>{t('ragSourceMapV4.statsTitle')} · {retrievedCount}/{visible.length}</StatsTitle>
      {data.clusterOrder.map(key => {
        const isBase = key === data.perustietopohjaKey;
        const label = isBase ? t('ragSourceMapV2.perustietopohja') : t(`ragSourceMapV2.voices.${key}`);
        const value = sums[key] || 0;
        return (
          <StatsBarRow key={key}>
            <StatsBarLabel title={label}>{label}</StatsBarLabel>
            <StatsBarTrack>
              <StatsBarFill style={{ width: `${(value / max) * 100}%`, background: data.clusterColors[key] }} />
            </StatsBarTrack>
            <StatsBarValue>{value}</StatsBarValue>
          </StatsBarRow>
        );
      })}
    </StatsPanel>
  );
}

export const DetailPanel = styled.div`
  position: absolute;
  top: 24px;
  right: 24px;
  width: 320px;
  max-width: calc(100% - 48px);
  max-height: calc(100% - 48px);
  overflow-y: auto;
  background: rgba(14, 14, 22, 0.88);
  border: 1px solid ${props => props.accent}55;
  border-radius: 10px;
  padding: 20px;
  color: white;
  font-family: ${MONO_FONT};
  backdrop-filter: blur(14px);
  z-index: 20;
  box-shadow: 0 0 30px ${props => props.accent}22;
`;

export const DetailTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 500;
  margin-bottom: 8px;
  padding-right: 20px;
  line-height: 1.35;
`;

export const DetailType = styled.div`
  font-size: 0.75rem;
  color: ${props => props.accent};
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

export const DetailMeta = styled.div`
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.55);
  margin-bottom: 10px;
`;

export const DetailDescription = styled.p`
  font-size: 0.8rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 16px;
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 12px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
  &:hover { color: white; }
`;

export const DetailTotal = styled.div`
  font-size: 1.6rem;
  font-weight: 600;
  margin-bottom: 2px;
`;

export const DetailTotalLabel = styled.div`
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 12px;
`;

export const ContextRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  padding: 6px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;
